import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import { Justify, Tabs } from 'tea-component';
import { scrollable } from 'tea-component/lib/table/addons';

import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import Loading from '@intelligence/components/Loading';

import { pickOperations } from '@sg/commonData/pickOperations';
import { xAxisHighLight, applyValueAxisBoundaryGap } from '@sg/utils/chatUtils';
import { mathFormat, financeFormat } from '@sg/utils/common';
import { mergeChartDataLabelStyle } from '@sg/utils/useChartOptions';
import { appendStackedTotalLabelSeries } from '@/pages/strategic/utils/stackedTotalLabel';
import { createBtnPoint, SGBuriedBtnPoint, StudiosTab } from '@/pages/strategic/utils/buriedPoint';

import {
  DefaultFinancialsChat,
  getStudioCardChartHeight,
  getStudioCardTableBodyMaxHeight,
  showFinancialsList,
  getFinancialTabs,
  FinancialsColumns,
} from '../../config';
import { useFinancialsData } from './hooks/useFinancialsData';
import { useBurnRateData } from '../../../component/BurnRateCard/hooks/useBurnRateData';
import { getBurnRateColumns } from '../../../component/BurnRateCard/config/columns';

type TViewStateKey = 'financials' | 'burnrate' | 'headcount' | 'staffrate';

interface IFinancialsCardProps {
  studioId: string;
  activeDateTypeSegment: string;
  selectedMonth?: string | null;
  smallChartViewState: Record<string, boolean>;
  updateStudioEyesViewState: (viewKey: TViewStateKey, enabled: boolean) => void;
  studioInfo: string;
}

const FinancialsCard: React.FC<IFinancialsCardProps> = ({
  studioId,
  activeDateTypeSegment,
  selectedMonth,
  smallChartViewState,
  updateStudioEyesViewState,
  studioInfo,
}) => {
  const { formatMessage } = useIntl();

  const [financialCardActive, setFinancialCardActive] = useState('financials');
  const [showFinancialsChatsType, setShowFinancialsChatsType] = useState<any>(DefaultFinancialsChat);

  const financialTabs = useMemo(() => getFinancialTabs(formatMessage), [formatMessage]);

  const activeFinancialViewKey = financialCardActive === 'burnrate' ? 'burnrate' : 'financials';
  const activeFinancialViewEnabled = !!smallChartViewState[activeFinancialViewKey];

  useEffect(() => {
    setShowFinancialsChatsType((prev: any) => ({
      ...prev,
      format: activeFinancialViewEnabled,
    }));
  }, [activeFinancialViewEnabled]);

  const {
    financialsData,
    financialsTableData,
    studioFinancialUpdateTime,
    financialsLoading,
  } = useFinancialsData(
    studioId,
    activeDateTypeSegment,
    showFinancialsChatsType?.value,
    !!showFinancialsChatsType?.format,
    selectedMonth,
  );

  const {
    burnRateData,
    burnRateTableData,
    burnRateUpdateTime,
    burnRateLoading,
  } = useBurnRateData(
    { scope: 'studio', studioId },
    activeDateTypeSegment,
    selectedMonth,
  );

  const burnRateColumns = useMemo(
    () => getBurnRateColumns('studio'),
    [],
  );

  const isBurnRate = financialCardActive === 'burnrate';
  const activeFinancialData = isBurnRate ? burnRateData : financialsData;
  const activeFinancialTableData = isBurnRate ? burnRateTableData : financialsTableData;
  const activeFinancialColumns = isBurnRate ? burnRateColumns : FinancialsColumns;
  const activeFinancialTitle = isBurnRate
    ? formatMessage({ id: 'dbs_burn_rate' })
    : formatMessage({ id: 'dbs_financials' });
  const activeFinancialUpdateTime = isBurnRate
    ? burnRateUpdateTime
    : studioFinancialUpdateTime;
  const activeFinancialLoading = isBurnRate ? burnRateLoading : financialsLoading;

  const financialChartHeight = useMemo(
    () => getStudioCardChartHeight(),
    [],
  );

  const financialTableBodyMaxHeight = useMemo(
    () => getStudioCardTableBodyMaxHeight({ tableHeaderRows: 1 }),
    [],
  );

  // for buried point
  const handleFinancialCardTabChange = (tabId: string) => {
    if (financialCardActive === tabId) {
      return;
    }
    const buttonKey = tabId === 'burnrate' ? 'burnRate' : 'financials';
    const buttonName = tabId === 'burnrate' ? 'Burn Rate' : 'Financials';
    const btnPoint = createBtnPoint(
      StudiosTab,
      buttonKey,
      `${buttonName} - ${studioInfo}`
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    setFinancialCardActive(tabId);
  };

  // Handle financials chart type change with buried point tracking
  const handleShowFinancialsChatsTypeChange = (e: any) => {
    const sectionName = isBurnRate ? 'Burn Rate' : 'Financials';
    const viewKey = isBurnRate ? 'burnrate' : 'financials';
    const currentViewEnabled = !!smallChartViewState[viewKey];

    if (e?.value) {
      const value = e.value;
      const isViewClick = value === 'view'
        || (e.format !== showFinancialsChatsType?.format && value === showFinancialsChatsType?.value);

      if (isViewClick) {
        const nextViewEnabled = !currentViewEnabled;
        const buttonKey = isBurnRate ? 'burnRateView' : 'studioFinancialsView';
        const btnPoint = createBtnPoint(
          StudiosTab,
          buttonKey,
          `${sectionName} - View - ${nextViewEnabled ? 'On' : 'Off'} - ${studioInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
        updateStudioEyesViewState(viewKey, nextViewEnabled);
        setShowFinancialsChatsType((prev: any) => ({
          ...prev,
          format: nextViewEnabled,
        }));
        return;
      }

      if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showFinancialsChatsType?.value !== 'bar') {
          const buttonKey = isBurnRate
            ? 'burnRateBarChart'
            : 'studioFinancialsBarChart';
          const btnPoint = createBtnPoint(
            StudiosTab,
            buttonKey,
            `${sectionName} - Bar Chart - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showFinancialsChatsType?.value !== 'table') {
          const buttonKey = isBurnRate
            ? 'burnRateTable'
            : 'studioFinancialsTable';
          const btnPoint = createBtnPoint(
            StudiosTab,
            buttonKey,
            `${sectionName} - Table - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Call the original setter
    setShowFinancialsChatsType({
      ...e,
      format: currentViewEnabled,
    });
  };

  return (
    <div className="dbs-by-date-card">
      <Justify
        bottom
        left={
          <div className="dbo-card-title dbs-by-date-card-title">
            <Tabs
              tabs={financialTabs}
              placement={"top"}
              activeId={financialCardActive}
              onActive={(e) => {
                handleFinancialCardTabChange(e.id);
              }}
            />
            <div className='dbo-card-title-sub'>
              {activeFinancialUpdateTime
                ? `${formatMessage({ id: 'dbs_update_on' })} ${activeFinancialUpdateTime}`
                : ''}
            </div>
          </div>
        }
        right={
          <RenderOperations
            key={`financial-ops-${financialCardActive}-${showFinancialsChatsType.value}-${activeFinancialViewEnabled}`}
            defaultValue={showFinancialsChatsType.value}
            defaultValueShow={showFinancialsChatsType.format}
            change={handleShowFinancialsChatsTypeChange}
            options={pickOperations(['view', ...showFinancialsList], formatMessage)}
            downloadParams={{
              data: activeFinancialTableData,
              columns: activeFinancialColumns,
              name: activeFinancialTitle,
            }}>
          </RenderOperations>
        }
      />

      {activeFinancialLoading && (
        <Loading style={{ height: financialChartHeight }} />
      )}

      {!activeFinancialLoading
        && showFinancialsChatsType?.value !== 'table' && (
        <Chart
          data={activeFinancialData[showFinancialsChatsType?.value] || {
            xAxisData: [],
            type: showFinancialsChatsType?.value,
            seriesData: [],
          }}
          key={`${financialCardActive}Financials${showFinancialsChatsType?.value}`}
          type={showFinancialsChatsType?.value}
          height={financialChartHeight}
          formatType={isBurnRate ? 'bmk' : 'million'}
          setOtherConfigFunc={(opt: any) => {
            opt.grid.containLabel = true;
            if (showFinancialsChatsType?.value === 'bar') {
              // Pad y-axis so bar/total labels are not clipped at the chart edge.
              applyValueAxisBoundaryGap(opt);
            }
            opt = xAxisHighLight(opt, {
              type: showFinancialsChatsType?.value === 'bar' ? 'shadow' : 'line',
              lineStyle: {
                color: '#BBBDCB',
                width: 1,
                type: 'solid',
              },
            } as any);

            if (Array.isArray(opt?.series)) {
              opt.series = opt.series.map((series: any) => {
                if (series?.type === 'line') {
                  // Keep line above bars, but below bar labels.
                  return {
                    ...series,
                    z: 2,
                    zlevel: 0,
                  };
                }
                if (series?.type === 'bar') {
                  return {
                    ...series,
                    z: 1,
                    zlevel: 0,
                  };
                }
                return series;
              });
            }

            if (
              !isBurnRate
              && showFinancialsChatsType?.value === 'bar'
              && activeFinancialViewEnabled
              && Array.isArray(opt?.series)
            ) {
              opt.series = opt.series.map((series: any) => {
                if (series?.type !== 'bar') {
                  return series;
                }
                return {
                  ...series,
                  label: mergeChartDataLabelStyle({
                    ...(series?.label || {}),
                    show: activeFinancialViewEnabled,
                    // Position is configured per data item (positive top / negative bottom).
                    distance: 8,
                    formatter: (params: any) => {
                      const value = params?.value?.value ?? params?.value;
                      if (value === null || value === undefined) return '';
                      return mathFormat(value);
                    },
                  }),
                };
              });
            }

            opt = appendStackedTotalLabelSeries(opt, {
              enabled:
                isBurnRate
                && showFinancialsChatsType?.value === 'bar'
                && activeFinancialViewEnabled,
              seriesName: '__total_label__',
              labelFormatter: (value: number) =>
                isBurnRate ? financeFormat(value, 1) : mathFormat(value),
            });

            return opt;
          }}
        />
      )}

      {!activeFinancialLoading
        && showFinancialsChatsType?.value === 'table' && (
        <TableContent
          className="dbs-commons-table dbs-chats-table"
          emptyMinHeight={financialChartHeight}
          datas={activeFinancialTableData}
          columns={activeFinancialColumns}
          sort={[{ by: 'fill_time', order: 'desc' }]}
          resultKey="list"
          recordKey={isBurnRate ? 'key' : 'date'}
          addons={[scrollable({
            // Fit burn-rate column widths so date and long headers stay on one line.
            minWidth: isBurnRate ? 580 : 600,
            maxHeight: financialTableBodyMaxHeight,
          })]}
        />
      )}
    </div>
  );
};

export default FinancialsCard;
